import { Asset, INDEXFORMAT_UINT32, SEMANTIC_POSITION } from 'playcanvas';
import type { AppBase } from 'playcanvas';

// Adapted from @playcanvas/supersplat-viewer collision internals. The package
// does not currently export these classes, so NSite keeps this scene-level copy.

interface PushOut {
    x: number;
    y: number;
    z: number;
}

interface RayHit {
    x: number;
    y: number;
    z: number;
}

interface SceneCollision {
    queryRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): RayHit | null;
    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean;
    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut): boolean;
    querySurfaceNormal(x: number, y: number, z: number, rdx: number, rdy: number, rdz: number): { nx: number; ny: number; nz: number };
}

interface VoxelMetadata {
    version?: string;
    gridBounds: { min: number[]; max: number[] };
    gaussianBounds?: { min: number[]; max: number[] };
    sceneBounds?: { min: number[]; max: number[] };
    voxelResolution: number;
    leafSize: number;
    treeDepth: number;
    nodeCount: number;
    leafDataCount: number;
}

const SOLID_LEAF_MARKER = 0xFF000000 >>> 0;
const PENETRATION_EPSILON = 1e-4;
const MAX_RESOLVE_ITERATIONS = 4;
const FLAT_R = 2;
const INV_SQRT2 = 1 / Math.sqrt(2);

const SURFACE_CANDIDATES: number[][] = [
    [1, 0, 0, 0, 1, 0, 0, 0, 1],
    [0, 1, 0, 1, 0, 0, 0, 0, 1],
    [0, 0, 1, 1, 0, 0, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, -1, 0, 1],
    [1, 0, -1, 0, 1, 0, 1, 0, 1],
    [1, 1, 0, 0, 0, 1, -1, 1, 0],
    [1, -1, 0, 0, 0, 1, 1, 1, 0],
    [0, 1, 1, 1, 0, 0, 0, -1, 1],
    [0, 1, -1, 1, 0, 0, 0, 1, 1]
];

const popcount = (n: number) => {
    n >>>= 0;
    n -= (n >>> 1) & 0x55555555;
    n = (n & 0x33333333) + ((n >>> 2) & 0x33333333);
    return (((n + (n >>> 4)) & 0x0F0F0F0F) * 0x01010101) >>> 24;
};

const resolveIterative = (
    cx: number,
    cy: number,
    cz: number,
    findPenetration: (cx: number, cy: number, cz: number, out: PushOut) => boolean,
    constraintNormals: PushOut[],
    scratch: PushOut,
    out: PushOut
) => {
    let resolvedX = cx;
    let resolvedY = cy;
    let resolvedZ = cz;
    let totalPushX = 0;
    let totalPushY = 0;
    let totalPushZ = 0;
    let hadCollision = false;
    let numNormals = 0;

    for (let iter = 0; iter < MAX_RESOLVE_ITERATIONS; iter++) {
        if (!findPenetration(resolvedX, resolvedY, resolvedZ, scratch)) break;
        hadCollision = true;

        let px = scratch.x;
        let py = scratch.y;
        let pz = scratch.z;

        for (let i = 0; i < numNormals; i++) {
            const n = constraintNormals[i];
            const dot = px * n.x + py * n.y + pz * n.z;
            if (dot < 0) {
                px -= dot * n.x;
                py -= dot * n.y;
                pz -= dot * n.z;
            }
        }

        const len = Math.sqrt(scratch.x * scratch.x + scratch.y * scratch.y + scratch.z * scratch.z);
        if (len > PENETRATION_EPSILON && numNormals < 3) {
            const n = constraintNormals[numNormals];
            const invLen = 1 / len;
            n.x = scratch.x * invLen;
            n.y = scratch.y * invLen;
            n.z = scratch.z * invLen;
            numNormals++;
        }

        resolvedX += px;
        resolvedY += py;
        resolvedZ += pz;
        totalPushX += px;
        totalPushY += py;
        totalPushZ += pz;
    }

    const totalPushSq = totalPushX * totalPushX + totalPushY * totalPushY + totalPushZ * totalPushZ;
    if (hadCollision && totalPushSq > PENETRATION_EPSILON * PENETRATION_EPSILON) {
        out.x = totalPushX;
        out.y = totalPushY;
        out.z = totalPushZ;
        return true;
    }
    return false;
};

class VoxelCollision implements SceneCollision {
    private _gridMinX: number;
    private _gridMinY: number;
    private _gridMinZ: number;
    private _numVoxelsX: number;
    private _numVoxelsY: number;
    private _numVoxelsZ: number;
    private _voxelResolution: number;
    private _leafSize: number;
    private _treeDepth: number;
    private _nodes: Uint32Array;
    private _leafData: Uint32Array;
    private readonly _push: PushOut = { x: 0, y: 0, z: 0 };
    private readonly _normalResult = { nx: 0, ny: 0, nz: 0 };
    private readonly _constraintNormals: PushOut[] = [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 }
    ];

    constructor(metadata: VoxelMetadata, nodes: Uint32Array, leafData: Uint32Array) {
        const res = metadata.voxelResolution;
        this._gridMinX = metadata.gridBounds.min[0];
        this._gridMinY = metadata.gridBounds.min[1];
        this._gridMinZ = metadata.gridBounds.min[2];
        this._numVoxelsX = Math.round((metadata.gridBounds.max[0] - metadata.gridBounds.min[0]) / res);
        this._numVoxelsY = Math.round((metadata.gridBounds.max[1] - metadata.gridBounds.min[1]) / res);
        this._numVoxelsZ = Math.round((metadata.gridBounds.max[2] - metadata.gridBounds.min[2]) / res);
        this._voxelResolution = res;
        this._leafSize = metadata.leafSize;
        this._treeDepth = metadata.treeDepth;
        this._nodes = nodes;
        this._leafData = leafData;
    }

    get gridMinX() {
        return this._gridMinX;
    }

    get gridMinY() {
        return this._gridMinY;
    }

    get gridMinZ() {
        return this._gridMinZ;
    }

    get numVoxelsX() {
        return this._numVoxelsX;
    }

    get numVoxelsY() {
        return this._numVoxelsY;
    }

    get numVoxelsZ() {
        return this._numVoxelsZ;
    }

    get voxelResolution() {
        return this._voxelResolution;
    }

    get leafSize() {
        return this._leafSize;
    }

    get treeDepth() {
        return this._treeDepth;
    }

    get nodes() {
        return this._nodes;
    }

    get leafData() {
        return this._leafData;
    }

    querySurfaceNormal(x: number, y: number, z: number, rdx: number, rdy: number, rdz: number) {
        const nudge = this._voxelResolution * 0.25;
        const ix = Math.floor((x + Math.sign(rdx) * nudge - this._gridMinX) / this._voxelResolution);
        const iy = Math.floor((y + Math.sign(rdy) * nudge - this._gridMinY) / this._voxelResolution);
        const iz = Math.floor((z + Math.sign(rdz) * nudge - this._gridMinZ) / this._voxelResolution);

        let bestScore = -1;
        let bestNx = 0;
        let bestNy = 1;
        let bestNz = 0;

        for (const cand of SURFACE_CANDIDATES) {
            const dx = cand[0];
            const dy = cand[1];
            const dz = cand[2];
            const dot = rdx * dx + rdy * dy + rdz * dz;
            if (Math.abs(dot) < 1e-6) continue;

            const sign = dot < 0 ? 1 : -1;
            const sx = dx * sign;
            const sy = dy * sign;
            const sz = dz * sign;
            const score = this.scoreSurfaceCandidate(ix, iy, iz, sx, sy, sz, cand);

            if (score > bestScore) {
                bestScore = score;
                const mag = (Math.abs(dx) + Math.abs(dy) + Math.abs(dz)) > 1 ? INV_SQRT2 : 1;
                bestNx = sx * mag;
                bestNy = sy * mag;
                bestNz = sz * mag;
            }
        }

        this._normalResult.nx = bestNx;
        this._normalResult.ny = bestNy;
        this._normalResult.nz = bestNz;
        return this._normalResult;
    }

    queryRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): RayHit | null {
        if (this._nodes.length === 0) return null;

        const res = this._voxelResolution;
        const gMinX = this._gridMinX;
        const gMinY = this._gridMinY;
        const gMinZ = this._gridMinZ;
        const gMaxX = gMinX + this._numVoxelsX * res;
        const gMaxY = gMinY + this._numVoxelsY * res;
        const gMaxZ = gMinZ + this._numVoxelsZ * res;
        const EPS = 1e-12;
        let tNear = 0;
        let tFar = maxDist;

        const slab = (origin: number, direction: number, min: number, max: number) => {
            if (Math.abs(direction) <= EPS) return origin >= min && origin < max;
            let t1 = (min - origin) / direction;
            let t2 = (max - origin) / direction;
            if (t1 > t2) {
                const tmp = t1;
                t1 = t2;
                t2 = tmp;
            }
            if (t1 > tNear) tNear = t1;
            tFar = Math.min(tFar, t2);
            return tNear <= tFar;
        };

        if (!slab(ox, dx, gMinX, gMaxX) || !slab(oy, dy, gMinY, gMaxY) || !slab(oz, dz, gMinZ, gMaxZ)) {
            return null;
        }

        const entryX = ox + dx * tNear;
        const entryY = oy + dy * tNear;
        const entryZ = oz + dz * tNear;
        let ix = Math.max(0, Math.min(Math.floor((entryX - gMinX) / res), this._numVoxelsX - 1));
        let iy = Math.max(0, Math.min(Math.floor((entryY - gMinY) / res), this._numVoxelsY - 1));
        let iz = Math.max(0, Math.min(Math.floor((entryZ - gMinZ) / res), this._numVoxelsZ - 1));

        const stepX = dx > 0 ? 1 : (dx < 0 ? -1 : 0);
        const stepY = dy > 0 ? 1 : (dy < 0 ? -1 : 0);
        const stepZ = dz > 0 ? 1 : (dz < 0 ? -1 : 0);
        const invDx = Math.abs(dx) > EPS ? 1 / dx : 0;
        const invDy = Math.abs(dy) > EPS ? 1 / dy : 0;
        const invDz = Math.abs(dz) > EPS ? 1 / dz : 0;
        let tMaxX = Math.abs(dx) > EPS ? (gMinX + (ix + (dx > 0 ? 1 : 0)) * res - ox) * invDx : Infinity;
        let tMaxY = Math.abs(dy) > EPS ? (gMinY + (iy + (dy > 0 ? 1 : 0)) * res - oy) * invDy : Infinity;
        let tMaxZ = Math.abs(dz) > EPS ? (gMinZ + (iz + (dz > 0 ? 1 : 0)) * res - oz) * invDz : Infinity;
        const tDeltaX = Math.abs(dx) > EPS ? res * Math.abs(invDx) : Infinity;
        const tDeltaY = Math.abs(dy) > EPS ? res * Math.abs(invDy) : Infinity;
        const tDeltaZ = Math.abs(dz) > EPS ? res * Math.abs(invDz) : Infinity;

        let currentT = tNear;
        const maxSteps = this._numVoxelsX + this._numVoxelsY + this._numVoxelsZ;

        for (let step = 0; step < maxSteps; step++) {
            if (this.isVoxelSolid(ix, iy, iz)) {
                return {
                    x: ox + dx * currentT,
                    y: oy + dy * currentT,
                    z: oz + dz * currentT
                };
            }

            if (tMaxX < tMaxY) {
                if (tMaxX < tMaxZ) {
                    currentT = tMaxX;
                    ix += stepX;
                    tMaxX += tDeltaX;
                } else {
                    currentT = tMaxZ;
                    iz += stepZ;
                    tMaxZ += tDeltaZ;
                }
            } else if (tMaxY < tMaxZ) {
                currentT = tMaxY;
                iy += stepY;
                tMaxY += tDeltaY;
            } else {
                currentT = tMaxZ;
                iz += stepZ;
                tMaxZ += tDeltaZ;
            }

            if (ix < 0 || iy < 0 || iz < 0 ||
                ix >= this._numVoxelsX || iy >= this._numVoxelsY || iz >= this._numVoxelsZ ||
                currentT > maxDist) {
                return null;
            }
        }

        return null;
    }

    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut) {
        if (this.nodes.length === 0) return false;
        return resolveIterative(
            cx,
            cy,
            cz,
            (rx, ry, rz, push) => this.resolveDeepestPenetration(rx, ry, rz, radius, push),
            this._constraintNormals,
            this._push,
            out
        );
    }

    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut) {
        if (this.nodes.length === 0) return false;
        return resolveIterative(
            cx,
            cy,
            cz,
            (rx, ry, rz, push) => this.resolveDeepestPenetrationCapsule(rx, ry, rz, halfHeight, radius, push),
            this._constraintNormals,
            this._push,
            out
        );
    }

    isVoxelSolid(ix: number, iy: number, iz: number) {
        if (this.nodes.length === 0 ||
            ix < 0 || iy < 0 || iz < 0 ||
            ix >= this.numVoxelsX || iy >= this.numVoxelsY || iz >= this.numVoxelsZ) {
            return false;
        }

        const blockX = Math.floor(ix / this.leafSize);
        const blockY = Math.floor(iy / this.leafSize);
        const blockZ = Math.floor(iz / this.leafSize);
        let nodeIndex = 0;

        for (let level = this.treeDepth - 1; level >= 0; level--) {
            const node = this.nodes[nodeIndex] >>> 0;
            if (node === SOLID_LEAF_MARKER) return true;

            const childMask = (node >>> 24) & 0xFF;
            if (childMask === 0) return this.checkLeafByIndex(node, ix, iy, iz);

            const bitX = (blockX >>> level) & 1;
            const bitY = (blockY >>> level) & 1;
            const bitZ = (blockZ >>> level) & 1;
            const octant = (bitZ << 2) | (bitY << 1) | bitX;
            if ((childMask & (1 << octant)) === 0) return false;

            const baseOffset = node & 0x00FFFFFF;
            const prefix = (1 << octant) - 1;
            nodeIndex = baseOffset + popcount(childMask & prefix);
        }

        const node = this.nodes[nodeIndex] >>> 0;
        return node === SOLID_LEAF_MARKER || this.checkLeafByIndex(node, ix, iy, iz);
    }

    private scoreSurfaceCandidate(
        ix: number,
        iy: number,
        iz: number,
        sx: number,
        sy: number,
        sz: number,
        cand: number[]
    ) {
        let best = 0;
        for (let depth = 1; depth >= -1; depth--) {
            let s = 0;
            for (let da = -FLAT_R; da <= FLAT_R; da++) {
                for (let db = -FLAT_R; db <= FLAT_R; db++) {
                    const px = ix + da * cand[3] + db * cand[6] - sx * depth;
                    const py = iy + da * cand[4] + db * cand[7] - sy * depth;
                    const pz = iz + da * cand[5] + db * cand[8] - sz * depth;
                    if (this.isVoxelSolid(px, py, pz) && !this.isVoxelSolid(px + sx, py + sy, pz + sz)) {
                        s++;
                    }
                }
            }
            if (s > best) best = s;
        }
        return best;
    }

    private resolveDeepestPenetration(cx: number, cy: number, cz: number, radius: number, out: PushOut) {
        const { voxelResolution, gridMinX, gridMinY, gridMinZ } = this;
        const radiusSq = radius * radius;
        const ixMin = Math.floor((cx - radius - gridMinX) / voxelResolution);
        const iyMin = Math.floor((cy - radius - gridMinY) / voxelResolution);
        const izMin = Math.floor((cz - radius - gridMinZ) / voxelResolution);
        const ixMax = Math.floor((cx + radius - gridMinX) / voxelResolution);
        const iyMax = Math.floor((cy + radius - gridMinY) / voxelResolution);
        const izMax = Math.floor((cz + radius - gridMinZ) / voxelResolution);
        let bestPushX = 0;
        let bestPushY = 0;
        let bestPushZ = 0;
        let bestPenetration = PENETRATION_EPSILON;
        let found = false;

        for (let iz = izMin; iz <= izMax; iz++) {
            for (let iy = iyMin; iy <= iyMax; iy++) {
                for (let ix = ixMin; ix <= ixMax; ix++) {
                    if (!this.isVoxelSolid(ix, iy, iz)) continue;

                    const vMinX = gridMinX + ix * voxelResolution;
                    const vMinY = gridMinY + iy * voxelResolution;
                    const vMinZ = gridMinZ + iz * voxelResolution;
                    const vMaxX = vMinX + voxelResolution;
                    const vMaxY = vMinY + voxelResolution;
                    const vMaxZ = vMinZ + voxelResolution;
                    const nearX = Math.max(vMinX, Math.min(cx, vMaxX));
                    const nearY = Math.max(vMinY, Math.min(cy, vMaxY));
                    const nearZ = Math.max(vMinZ, Math.min(cz, vMaxZ));
                    const dx = cx - nearX;
                    const dy = cy - nearY;
                    const dz = cz - nearZ;
                    const distSq = dx * dx + dy * dy + dz * dz;
                    if (distSq >= radiusSq) continue;

                    const push = this.computeAabbSpherePush(cx, cy, cz, radius, distSq, dx, dy, dz, vMinX, vMinY, vMinZ, vMaxX, vMaxY, vMaxZ);
                    if (push.penetration > bestPenetration) {
                        bestPenetration = push.penetration;
                        bestPushX = push.x;
                        bestPushY = push.y;
                        bestPushZ = push.z;
                        found = true;
                    }
                }
            }
        }

        if (found) {
            out.x = bestPushX;
            out.y = bestPushY;
            out.z = bestPushZ;
        }
        return found;
    }

    private resolveDeepestPenetrationCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut) {
        const { voxelResolution, gridMinX, gridMinY, gridMinZ } = this;
        const radiusSq = radius * radius;
        const segBottomY = cy - halfHeight;
        const segTopY = cy + halfHeight;
        const ixMin = Math.floor((cx - radius - gridMinX) / voxelResolution);
        const iyMin = Math.floor((segBottomY - radius - gridMinY) / voxelResolution);
        const izMin = Math.floor((cz - radius - gridMinZ) / voxelResolution);
        const ixMax = Math.floor((cx + radius - gridMinX) / voxelResolution);
        const iyMax = Math.floor((segTopY + radius - gridMinY) / voxelResolution);
        const izMax = Math.floor((cz + radius - gridMinZ) / voxelResolution);
        let bestPushX = 0;
        let bestPushY = 0;
        let bestPushZ = 0;
        let bestPenetration = PENETRATION_EPSILON;
        let found = false;

        for (let iz = izMin; iz <= izMax; iz++) {
            for (let iy = iyMin; iy <= iyMax; iy++) {
                for (let ix = ixMin; ix <= ixMax; ix++) {
                    if (!this.isVoxelSolid(ix, iy, iz)) continue;

                    const vMinX = gridMinX + ix * voxelResolution;
                    const vMinY = gridMinY + iy * voxelResolution;
                    const vMinZ = gridMinZ + iz * voxelResolution;
                    const vMaxX = vMinX + voxelResolution;
                    const vMaxY = vMinY + voxelResolution;
                    const vMaxZ = vMinZ + voxelResolution;
                    let segY: number;
                    if (segTopY < vMinY) {
                        segY = segTopY;
                    } else if (segBottomY > vMaxY) {
                        segY = segBottomY;
                    } else {
                        segY = Math.max(segBottomY, Math.min(segTopY, (vMinY + vMaxY) * 0.5));
                    }

                    const nearX = Math.max(vMinX, Math.min(cx, vMaxX));
                    const nearY = Math.max(vMinY, Math.min(segY, vMaxY));
                    const nearZ = Math.max(vMinZ, Math.min(cz, vMaxZ));
                    const dx = cx - nearX;
                    const dy = segY - nearY;
                    const dz = cz - nearZ;
                    const distSq = dx * dx + dy * dy + dz * dz;
                    if (distSq >= radiusSq) continue;

                    const push = this.computeAabbSpherePush(cx, segY, cz, radius, distSq, dx, dy, dz, vMinX, vMinY, vMinZ, vMaxX, vMaxY, vMaxZ);
                    if (push.penetration > bestPenetration) {
                        bestPenetration = push.penetration;
                        bestPushX = push.x;
                        bestPushY = push.y;
                        bestPushZ = push.z;
                        found = true;
                    }
                }
            }
        }

        if (found) {
            out.x = bestPushX;
            out.y = bestPushY;
            out.z = bestPushZ;
        }
        return found;
    }

    private computeAabbSpherePush(
        cx: number,
        cy: number,
        cz: number,
        radius: number,
        distSq: number,
        dx: number,
        dy: number,
        dz: number,
        vMinX: number,
        vMinY: number,
        vMinZ: number,
        vMaxX: number,
        vMaxY: number,
        vMaxZ: number
    ) {
        if (distSq > 1e-12) {
            const dist = Math.sqrt(distSq);
            const penetration = radius - dist;
            const invDist = 1 / dist;
            return {
                x: dx * invDist * penetration,
                y: dy * invDist * penetration,
                z: dz * invDist * penetration,
                penetration
            };
        }

        const escapeX = cx - vMinX < vMaxX - cx ? -(cx - vMinX + radius) : (vMaxX - cx + radius);
        const escapeY = cy - vMinY < vMaxY - cy ? -(cy - vMinY + radius) : (vMaxY - cy + radius);
        const escapeZ = cz - vMinZ < vMaxZ - cz ? -(cz - vMinZ + radius) : (vMaxZ - cz + radius);
        const absX = Math.abs(escapeX);
        const absY = Math.abs(escapeY);
        const absZ = Math.abs(escapeZ);
        if (absX <= absY && absX <= absZ) return { x: escapeX, y: 0, z: 0, penetration: absX };
        if (absY <= absZ) return { x: 0, y: escapeY, z: 0, penetration: absY };
        return { x: 0, y: 0, z: escapeZ, penetration: absZ };
    }

    private checkLeafByIndex(node: number, ix: number, iy: number, iz: number) {
        const leafDataIndex = node & 0x00FFFFFF;
        const vx = ix & 3;
        const vy = iy & 3;
        const vz = iz & 3;
        const bitIndex = vz * 16 + vy * 4 + vx;
        if (bitIndex < 32) {
            const lo = this.leafData[leafDataIndex * 2] >>> 0;
            return ((lo >>> bitIndex) & 1) === 1;
        }
        const hi = this.leafData[leafDataIndex * 2 + 1] >>> 0;
        return ((hi >>> (bitIndex - 32)) & 1) === 1;
    }
}

class FlippedVoxelCollision extends VoxelCollision {
    querySurfaceNormal(x: number, y: number, z: number, rdx: number, rdy: number, rdz: number) {
        const result = super.querySurfaceNormal(-x, -y, z, -rdx, -rdy, rdz);
        result.nx = -result.nx;
        result.ny = -result.ny;
        return result;
    }

    queryRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number) {
        const hit = super.queryRay(-ox, -oy, oz, -dx, -dy, dz, maxDist);
        if (hit) {
            hit.x = -hit.x;
            hit.y = -hit.y;
        }
        return hit;
    }

    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut) {
        const result = super.querySphere(-cx, -cy, cz, radius, out);
        if (result) {
            out.x = -out.x;
            out.y = -out.y;
        }
        return result;
    }

    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut) {
        const result = super.queryCapsule(-cx, -cy, cz, halfHeight, radius, out);
        if (result) {
            out.x = -out.x;
            out.y = -out.y;
        }
        return result;
    }
}

type TriangleData = {
    v0x: Float32Array; v0y: Float32Array; v0z: Float32Array;
    v1x: Float32Array; v1y: Float32Array; v1z: Float32Array;
    v2x: Float32Array; v2y: Float32Array; v2z: Float32Array;
    nx: Float32Array; ny: Float32Array; nz: Float32Array;
    indices: Uint32Array;
    count: number;
};

type BvhNode = {
    minX: number; minY: number; minZ: number;
    maxX: number; maxY: number; maxZ: number;
    left: BvhNode | null;
    right: BvhNode | null;
    triStart: number;
    triCount: number;
};

const meshScratch = {
    triBounds: { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 },
    push: { x: 0, y: 0, z: 0 },
    normals: [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 }
    ]
};

const computeTriangleBounds = (tris: TriangleData, idx: number, out: { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number }) => {
    const i = tris.indices[idx];
    out.minX = Math.min(tris.v0x[i], tris.v1x[i], tris.v2x[i]);
    out.minY = Math.min(tris.v0y[i], tris.v1y[i], tris.v2y[i]);
    out.minZ = Math.min(tris.v0z[i], tris.v1z[i], tris.v2z[i]);
    out.maxX = Math.max(tris.v0x[i], tris.v1x[i], tris.v2x[i]);
    out.maxY = Math.max(tris.v0y[i], tris.v1y[i], tris.v2y[i]);
    out.maxZ = Math.max(tris.v0z[i], tris.v1z[i], tris.v2z[i]);
};

const buildBvh = (tris: TriangleData, start: number, count: number): BvhNode => {
    const bounds = { minX: Infinity, minY: Infinity, minZ: Infinity, maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity };
    const tb = meshScratch.triBounds;
    for (let i = start; i < start + count; i++) {
        computeTriangleBounds(tris, i, tb);
        bounds.minX = Math.min(bounds.minX, tb.minX);
        bounds.minY = Math.min(bounds.minY, tb.minY);
        bounds.minZ = Math.min(bounds.minZ, tb.minZ);
        bounds.maxX = Math.max(bounds.maxX, tb.maxX);
        bounds.maxY = Math.max(bounds.maxY, tb.maxY);
        bounds.maxZ = Math.max(bounds.maxZ, tb.maxZ);
    }
    if (count <= 4) {
        return { ...bounds, left: null, right: null, triStart: start, triCount: count };
    }

    const dx = bounds.maxX - bounds.minX;
    const dy = bounds.maxY - bounds.minY;
    const dz = bounds.maxZ - bounds.minZ;
    const axis = dx >= dy && dx >= dz ? 0 : (dy >= dz ? 1 : 2);
    const mid = axis === 0 ? (bounds.minX + bounds.maxX) * 0.5 :
        axis === 1 ? (bounds.minY + bounds.maxY) * 0.5 :
            (bounds.minZ + bounds.maxZ) * 0.5;
    let left = start;
    let right = start + count - 1;
    while (left <= right) {
        const i = tris.indices[left];
        const c = axis === 0 ? (tris.v0x[i] + tris.v1x[i] + tris.v2x[i]) / 3 :
            axis === 1 ? (tris.v0y[i] + tris.v1y[i] + tris.v2y[i]) / 3 :
                (tris.v0z[i] + tris.v1z[i] + tris.v2z[i]) / 3;
        if (c < mid) {
            left++;
        } else {
            const tmp = tris.indices[left];
            tris.indices[left] = tris.indices[right];
            tris.indices[right] = tmp;
            right--;
        }
    }
    let leftCount = left - start;
    if (leftCount === 0 || leftCount === count) leftCount = count >> 1;
    return {
        ...bounds,
        left: buildBvh(tris, start, leftCount),
        right: buildBvh(tris, start + leftCount, count - leftCount),
        triStart: 0,
        triCount: 0
    };
};

const rayAabb = (
    ox: number, oy: number, oz: number,
    idx: number, idy: number, idz: number,
    minX: number, minY: number, minZ: number,
    maxX: number, maxY: number, maxZ: number,
    maxDist: number
) => {
    const t1x = (minX - ox) * idx;
    const t2x = (maxX - ox) * idx;
    const t1y = (minY - oy) * idy;
    const t2y = (maxY - oy) * idy;
    const t1z = (minZ - oz) * idz;
    const t2z = (maxZ - oz) * idz;
    const tmin = Math.max(Math.min(t1x, t2x), Math.min(t1y, t2y), Math.min(t1z, t2z));
    const tmax = Math.min(Math.max(t1x, t2x), Math.max(t1y, t2y), Math.max(t1z, t2z));
    if (tmax < 0 || tmin > tmax || tmin > maxDist) return -1;
    return tmin >= 0 ? tmin : 0;
};

const rayTriangle = (
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number
) => {
    const e1x = bx - ax;
    const e1y = by - ay;
    const e1z = bz - az;
    const e2x = cx - ax;
    const e2y = cy - ay;
    const e2z = cz - az;
    const hx = dy * e2z - dz * e2y;
    const hy = dz * e2x - dx * e2z;
    const hz = dx * e2y - dy * e2x;
    const a = e1x * hx + e1y * hy + e1z * hz;
    if (a > -1e-6 && a < 1e-6) return null;
    const f = 1 / a;
    const sx = ox - ax;
    const sy = oy - ay;
    const sz = oz - az;
    const u = f * (sx * hx + sy * hy + sz * hz);
    if (u < 0 || u > 1) return null;
    const qx = sy * e1z - sz * e1y;
    const qy = sz * e1x - sx * e1z;
    const qz = sx * e1y - sy * e1x;
    const v = f * (dx * qx + dy * qy + dz * qz);
    if (v < 0 || u + v > 1) return null;
    const t = f * (e2x * qx + e2y * qy + e2z * qz);
    return t > 1e-6 ? t : null;
};

const sphereAabbOverlap = (
    cx: number, cy: number, cz: number, radius: number,
    minX: number, minY: number, minZ: number,
    maxX: number, maxY: number, maxZ: number
) => {
    const x = Math.max(minX, Math.min(cx, maxX));
    const y = Math.max(minY, Math.min(cy, maxY));
    const z = Math.max(minZ, Math.min(cz, maxZ));
    const dx = cx - x;
    const dy = cy - y;
    const dz = cz - z;
    return dx * dx + dy * dy + dz * dz <= radius * radius;
};

const closestPointOnTriangle = (
    px: number, py: number, pz: number,
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    out: PushOut
) => {
    const abx = bx - ax; const aby = by - ay; const abz = bz - az;
    const acx = cx - ax; const acy = cy - ay; const acz = cz - az;
    const apx = px - ax; const apy = py - ay; const apz = pz - az;
    const d1 = abx * apx + aby * apy + abz * apz;
    const d2 = acx * apx + acy * apy + acz * apz;
    if (d1 <= 0 && d2 <= 0) {
        out.x = ax; out.y = ay; out.z = az; return;
    }
    const bpx = px - bx; const bpy = py - by; const bpz = pz - bz;
    const d3 = abx * bpx + aby * bpy + abz * bpz;
    const d4 = acx * bpx + acy * bpy + acz * bpz;
    if (d3 >= 0 && d4 <= d3) {
        out.x = bx; out.y = by; out.z = bz; return;
    }
    const vc = d1 * d4 - d3 * d2;
    if (vc <= 0 && d1 >= 0 && d3 <= 0) {
        const v = d1 / (d1 - d3);
        out.x = ax + v * abx; out.y = ay + v * aby; out.z = az + v * abz; return;
    }
    const cpx = px - cx; const cpy = py - cy; const cpz = pz - cz;
    const d5 = abx * cpx + aby * cpy + abz * cpz;
    const d6 = acx * cpx + acy * cpy + acz * cpz;
    if (d6 >= 0 && d5 <= d6) {
        out.x = cx; out.y = cy; out.z = cz; return;
    }
    const vb = d5 * d2 - d1 * d6;
    if (vb <= 0 && d2 >= 0 && d6 <= 0) {
        const w = d2 / (d2 - d6);
        out.x = ax + w * acx; out.y = ay + w * acy; out.z = az + w * acz; return;
    }
    const va = d3 * d6 - d5 * d4;
    if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
        const w = (d4 - d3) / (d4 - d3 + d5 - d6);
        out.x = bx + w * (cx - bx); out.y = by + w * (cy - by); out.z = bz + w * (cz - bz); return;
    }
    const denom = 1 / (va + vb + vc);
    const v = vb * denom;
    const w = vc * denom;
    out.x = ax + abx * v + acx * w;
    out.y = ay + aby * v + acy * w;
    out.z = az + abz * v + acz * w;
};

const pointSegmentDistanceSq = (
    px: number, py: number, pz: number,
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    out: PushOut
) => {
    const abx = bx - ax;
    const aby = by - ay;
    const abz = bz - az;
    const lenSq = abx * abx + aby * aby + abz * abz;
    const t = lenSq < 1e-20 ? 0 : Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby + (pz - az) * abz) / lenSq));
    out.x = ax + abx * t;
    out.y = ay + aby * t;
    out.z = az + abz * t;
    const dx = px - out.x;
    const dy = py - out.y;
    const dz = pz - out.z;
    return dx * dx + dy * dy + dz * dz;
};

class MeshCollision implements SceneCollision {
    private _tris: TriangleData;
    private _root: BvhNode;
    private _stack: BvhNode[] = [];
    private readonly _normalResult = { nx: 0, ny: 1, nz: 0 };

    constructor(positions: Float32Array, indices: Uint32Array) {
        const triCount = Math.floor(indices.length / 3);
        const tris: TriangleData = {
            v0x: new Float32Array(triCount),
            v0y: new Float32Array(triCount),
            v0z: new Float32Array(triCount),
            v1x: new Float32Array(triCount),
            v1y: new Float32Array(triCount),
            v1z: new Float32Array(triCount),
            v2x: new Float32Array(triCount),
            v2y: new Float32Array(triCount),
            v2z: new Float32Array(triCount),
            nx: new Float32Array(triCount),
            ny: new Float32Array(triCount),
            nz: new Float32Array(triCount),
            indices: new Uint32Array(triCount),
            count: triCount
        };
        for (let t = 0; t < triCount; t++) {
            const i0 = indices[t * 3] * 3;
            const i1 = indices[t * 3 + 1] * 3;
            const i2 = indices[t * 3 + 2] * 3;
            tris.v0x[t] = positions[i0]; tris.v0y[t] = positions[i0 + 1]; tris.v0z[t] = positions[i0 + 2];
            tris.v1x[t] = positions[i1]; tris.v1y[t] = positions[i1 + 1]; tris.v1z[t] = positions[i1 + 2];
            tris.v2x[t] = positions[i2]; tris.v2y[t] = positions[i2 + 1]; tris.v2z[t] = positions[i2 + 2];
            const ux = tris.v1x[t] - tris.v0x[t]; const uy = tris.v1y[t] - tris.v0y[t]; const uz = tris.v1z[t] - tris.v0z[t];
            const vx = tris.v2x[t] - tris.v0x[t]; const vy = tris.v2y[t] - tris.v0y[t]; const vz = tris.v2z[t] - tris.v0z[t];
            let nx = uy * vz - uz * vy; let ny = uz * vx - ux * vz; let nz = ux * vy - uy * vx;
            const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
            nx /= len; ny /= len; nz /= len;
            tris.nx[t] = nx; tris.ny[t] = ny; tris.nz[t] = nz;
            tris.indices[t] = t;
        }
        this._tris = tris;
        this._root = buildBvh(tris, 0, triCount);
    }

    queryRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number) {
        const idx = Math.abs(dx) > 1e-12 ? 1 / dx : 1e30;
        const idy = Math.abs(dy) > 1e-12 ? 1 / dy : 1e30;
        const idz = Math.abs(dz) > 1e-12 ? 1 / dz : 1e30;
        let best = maxDist;
        const stack = this._stack;
        let top = 0;
        stack[top++] = this._root;
        while (top > 0) {
            const node = stack[--top];
            if (rayAabb(ox, oy, oz, idx, idy, idz, node.minX, node.minY, node.minZ, node.maxX, node.maxY, node.maxZ, best) < 0) continue;
            if (node.left === null) {
                for (let j = node.triStart; j < node.triStart + node.triCount; j++) {
                    const i = this._tris.indices[j];
                    const t = rayTriangle(
                        ox, oy, oz, dx, dy, dz,
                        this._tris.v0x[i], this._tris.v0y[i], this._tris.v0z[i],
                        this._tris.v1x[i], this._tris.v1y[i], this._tris.v1z[i],
                        this._tris.v2x[i], this._tris.v2y[i], this._tris.v2z[i]
                    );
                    if (t !== null && t < best) best = t;
                }
            } else {
                stack[top++] = node.right;
                stack[top++] = node.left;
            }
        }
        return best < maxDist ? { x: ox + dx * best, y: oy + dy * best, z: oz + dz * best } : null;
    }

    querySurfaceNormal(x: number, y: number, z: number, rdx: number, rdy: number, rdz: number) {
        const hit = this.findNearestTriangle(x, y, z, Math.max(0.2, 0.5), rdx, rdy, rdz);
        if (!hit) {
            this._normalResult.nx = 0;
            this._normalResult.ny = 1;
            this._normalResult.nz = 0;
            return this._normalResult;
        }
        this._normalResult.nx = hit.nx;
        this._normalResult.ny = hit.ny;
        this._normalResult.nz = hit.nz;
        return this._normalResult;
    }

    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut) {
        return resolveIterative(
            cx,
            cy,
            cz,
            (rx, ry, rz, push) => this.deepestSpherePenetration(rx, ry, rz, radius, push),
            meshScratch.normals,
            meshScratch.push,
            out
        );
    }

    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut) {
        return resolveIterative(
            cx,
            cy,
            cz,
            (rx, ry, rz, push) => this.deepestCapsulePenetration(rx, ry, rz, halfHeight, radius, push),
            meshScratch.normals,
            meshScratch.push,
            out
        );
    }

    private findNearestTriangle(x: number, y: number, z: number, radius: number, rdx: number, rdy: number, rdz: number) {
        let bestIdx = -1;
        let bestDot = -Infinity;
        const stack = this._stack;
        let top = 0;
        stack[top++] = this._root;
        while (top > 0) {
            const node = stack[--top];
            if (!sphereAabbOverlap(x, y, z, radius, node.minX, node.minY, node.minZ, node.maxX, node.maxY, node.maxZ)) continue;
            if (node.left === null) {
                for (let j = node.triStart; j < node.triStart + node.triCount; j++) {
                    const i = this._tris.indices[j];
                    const dot = -(this._tris.nx[i] * rdx + this._tris.ny[i] * rdy + this._tris.nz[i] * rdz);
                    if (dot > bestDot) {
                        bestDot = dot;
                        bestIdx = i;
                    }
                }
            } else {
                stack[top++] = node.right;
                stack[top++] = node.left;
            }
        }
        return bestIdx >= 0 ? { nx: this._tris.nx[bestIdx], ny: this._tris.ny[bestIdx], nz: this._tris.nz[bestIdx] } : null;
    }

    private deepestSpherePenetration(cx: number, cy: number, cz: number, radius: number, out: PushOut) {
        let bestPen = PENETRATION_EPSILON;
        let bestPx = 0; let bestPy = 0; let bestPz = 0;
        let found = false;
        const stack = this._stack;
        let top = 0;
        stack[top++] = this._root;
        while (top > 0) {
            const node = stack[--top];
            if (!sphereAabbOverlap(cx, cy, cz, radius, node.minX, node.minY, node.minZ, node.maxX, node.maxY, node.maxZ)) continue;
            if (node.left === null) {
                for (let j = node.triStart; j < node.triStart + node.triCount; j++) {
                    const i = this._tris.indices[j];
                    closestPointOnTriangle(
                        cx, cy, cz,
                        this._tris.v0x[i], this._tris.v0y[i], this._tris.v0z[i],
                        this._tris.v1x[i], this._tris.v1y[i], this._tris.v1z[i],
                        this._tris.v2x[i], this._tris.v2y[i], this._tris.v2z[i],
                        meshScratch.push
                    );
                    const dx = cx - meshScratch.push.x;
                    const dy = cy - meshScratch.push.y;
                    const dz = cz - meshScratch.push.z;
                    const distSq = dx * dx + dy * dy + dz * dz;
                    if (distSq >= radius * radius) continue;
                    const dist = Math.sqrt(distSq);
                    const penetration = radius - dist;
                    if (penetration > bestPen) {
                        bestPen = penetration;
                        if (dist > 1e-10) {
                            const invDist = 1 / dist;
                            bestPx = dx * invDist * penetration;
                            bestPy = dy * invDist * penetration;
                            bestPz = dz * invDist * penetration;
                        } else {
                            bestPx = this._tris.nx[i] * penetration;
                            bestPy = this._tris.ny[i] * penetration;
                            bestPz = this._tris.nz[i] * penetration;
                        }
                        found = true;
                    }
                }
            } else {
                stack[top++] = node.right;
                stack[top++] = node.left;
            }
        }
        if (found) {
            out.x = bestPx;
            out.y = bestPy;
            out.z = bestPz;
        }
        return found;
    }

    private deepestCapsulePenetration(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut) {
        let bestPen = PENETRATION_EPSILON;
        let bestPx = 0; let bestPy = 0; let bestPz = 0;
        let found = false;
        const stack = this._stack;
        let top = 0;
        stack[top++] = this._root;
        const minX = cx - radius;
        const maxX = cx + radius;
        const minY = cy - halfHeight - radius;
        const maxY = cy + halfHeight + radius;
        const minZ = cz - radius;
        const maxZ = cz + radius;
        const closest = { x: 0, y: 0, z: 0 };
        while (top > 0) {
            const node = stack[--top];
            if (maxX < node.minX || minX > node.maxX || maxY < node.minY || minY > node.maxY || maxZ < node.minZ || minZ > node.maxZ) continue;
            if (node.left === null) {
                for (let j = node.triStart; j < node.triStart + node.triCount; j++) {
                    const i = this._tris.indices[j];
                    const SAMPLES = 5;
                    for (let s = 0; s <= SAMPLES; s++) {
                        const t = s / SAMPLES;
                        const sy = cy - halfHeight + (halfHeight * 2) * t;
                        closestPointOnTriangle(
                            cx, sy, cz,
                            this._tris.v0x[i], this._tris.v0y[i], this._tris.v0z[i],
                            this._tris.v1x[i], this._tris.v1y[i], this._tris.v1z[i],
                            this._tris.v2x[i], this._tris.v2y[i], this._tris.v2z[i],
                            closest
                        );
                        const distSq = pointSegmentDistanceSq(
                            closest.x, closest.y, closest.z,
                            cx, cy - halfHeight, cz,
                            cx, cy + halfHeight, cz,
                            meshScratch.push
                        );
                        if (distSq >= radius * radius) continue;
                        const dx = meshScratch.push.x - closest.x;
                        const dy = meshScratch.push.y - closest.y;
                        const dz = meshScratch.push.z - closest.z;
                        const dist = Math.sqrt(distSq);
                        const penetration = radius - dist;
                        if (penetration > bestPen) {
                            bestPen = penetration;
                            if (dist > 1e-10) {
                                const invDist = 1 / dist;
                                bestPx = dx * invDist * penetration;
                                bestPy = dy * invDist * penetration;
                                bestPz = dz * invDist * penetration;
                            } else {
                                bestPx = this._tris.nx[i] * penetration;
                                bestPy = this._tris.ny[i] * penetration;
                                bestPz = this._tris.nz[i] * penetration;
                            }
                            found = true;
                        }
                    }
                }
            } else {
                stack[top++] = node.right;
                stack[top++] = node.left;
            }
        }
        if (found) {
            out.x = bestPx;
            out.y = bestPy;
            out.z = bestPz;
        }
        return found;
    }

    static fromGlb(app: AppBase, url: string): Promise<MeshCollision> {
        return new Promise((resolve, reject) => {
            const asset = new Asset(url, 'container', { url });
            const cleanup = () => {
                app.assets.remove(asset);
                asset.unload();
            };

            asset.on('load', () => {
                const renders = (asset.resource as any).renders as any[];
                if (!renders?.length) {
                    cleanup();
                    reject(new Error('GLB contains no mesh data'));
                    return;
                }

                const allPositions: number[] = [];
                const allIndices: number[] = [];
                let vertexOffset = 0;

                for (const renderAsset of renders) {
                    const render = renderAsset.resource;
                    for (const mesh of render.meshes) {
                        const vb = mesh.vertexBuffer;
                        const ib = mesh.indexBuffer[0];
                        if (!vb || !ib) continue;

                        const format = vb.format;
                        const posElement = format.elements.find((element: any) => element.name === SEMANTIC_POSITION);
                        if (!posElement) continue;

                        const data = new Float32Array(vb.storage);
                        const stride = format.size / 4;
                        const offset = posElement.offset / 4;
                        const numVerts = vb.numVertices;
                        for (let v = 0; v < numVerts; v++) {
                            const base = v * stride + offset;
                            allPositions.push(data[base], data[base + 1], data[base + 2]);
                        }

                        const indexData = ib.format === INDEXFORMAT_UINT32 ? new Uint32Array(ib.storage) : new Uint16Array(ib.storage);
                        for (const prim of mesh.primitive) {
                            for (let i = 0; i < prim.count; i++) {
                                allIndices.push(indexData[prim.base + i] + vertexOffset);
                            }
                        }
                        vertexOffset += numVerts;
                    }
                }

                if (allIndices.length === 0) {
                    cleanup();
                    reject(new Error('GLB meshes contain no triangle data'));
                    return;
                }

                const collision = new MeshCollision(new Float32Array(allPositions), new Uint32Array(allIndices));
                cleanup();
                resolve(collision);
            });

            asset.on('error', (err: string) => {
                cleanup();
                reject(new Error(err));
            });

            app.assets.add(asset);
            app.assets.load(asset);
        });
    }
}

const createVoxelCollision = (metadata: VoxelMetadata, binary: ArrayBuffer) => {
    const view = new Uint32Array(binary);
    const nodes = view.slice(0, metadata.nodeCount);
    const leafData = view.slice(metadata.nodeCount, metadata.nodeCount + metadata.leafDataCount);
    const isLegacy = !metadata.version || parseFloat(metadata.version) < 1.1;
    return isLegacy ? new FlippedVoxelCollision(metadata, nodes, leafData) : new VoxelCollision(metadata, nodes, leafData);
};

export {
    MeshCollision,
    VoxelCollision,
    createVoxelCollision
};

export type {
    PushOut,
    RayHit,
    SceneCollision,
    VoxelMetadata
};
